import { useEffect, useRef, useState } from 'react';

/**
 * Debounce a value with a given delay.
 * Source: https://dev.to/arnonate/debouncing-react-query-with-hooks-2ek6
 * @param value The value to debounce
 * @param delay How long in milliseconds to delay before "accepting" a value
 */
export function useDebounce<T>(value: T, delay = 500): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    // Cancel the timeout if value changes (also on delay change or unmount)
    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

/**
 * Hook to get the previous state of a value.
 * Source: https://reactjs.org/docs/hooks-faq.html#how-to-get-the-previous-props-or-state
 * @param value The value to get the previous state of
 */
export function usePreviousState<T>(value: T): T | undefined {
  const ref = useRef<T>();

  useEffect(() => {
    ref.current = value;
  }, [value]);

  return ref.current;
}
